import { env } from '@/config/env';
import { supabase } from '@/integrations/supabase/client';

interface TokenData {
  accessToken: string | null;
  recoveryMode: boolean;
  hasCode: boolean;
  error: string | null;
}

interface TokenExtractResult {
  tokenData: TokenData;
  errorMessage: string | null;
  isResetMode: boolean;
}

/**
 * Extracts auth tokens from URL fragments and query parameters
 * Handles both hash-based tokens and PKCE code flow
 */
export const extractTokenFromUrl = (url?: string): TokenData => {
  try {
    // Use provided URL or current window location
    const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    
    if (!currentUrl) {
      return { 
        accessToken: null, 
        recoveryMode: false, 
        hasCode: false,
        error: 'No URL available'
      };
    }
    
    // Parse the URL - handle potential URL parsing errors
    let urlObj: URL;
    try {
      urlObj = new URL(currentUrl);
    } catch (urlError) {
      console.error('Invalid URL format:', urlError);
      return { 
        accessToken: null, 
        recoveryMode: false, 
        hasCode: false,
        error: 'Invalid URL format'
      };
    }
    
    const hash = urlObj.hash;
    const searchParams = new URLSearchParams(urlObj.search);
    
    // Process hash fragment parameters (remove # if present)
    const hashParams = new URLSearchParams(
      hash.startsWith('#') ? hash.substring(1) : hash
    );
    
    // Log the hash and search params for debugging
    console.log('URL Processing:', {
      fullUrl: currentUrl,
      hash: hash,
      search: urlObj.search,
      hashParamsCount: Array.from(hashParams.entries()).length,
      searchParamsCount: Array.from(searchParams.entries()).length
    });
    
    // Check for access_token in hash - try different methods if needed
    let accessToken = hashParams.get('access_token');
    
    // If access token not found in standard location, try legacy token format
    if (!accessToken && hash.includes('access_token=')) {
      const tokenMatch = hash.match(/access_token=([^&]+)/);
      if (tokenMatch && tokenMatch[1]) {
        accessToken = tokenMatch[1];
        console.log('Found access token using regex match');
      }
    }
    
    // Check for recovery mode
    const isRecoveryMode = hashParams.get('type') === 'recovery';
    
    // Check for code parameter (PKCE flow)
    const hasCode = searchParams.has('code');
    
    // Check for errors
    let error = null;
    
    // First check hash for errors
    if (hashParams.has('error') || hashParams.has('error_description')) {
      error = hashParams.get('error_description') || hashParams.get('error') || 
              'Error in authentication parameters';
    }
    
    // Then check search params for errors
    if (!error && (searchParams.has('error') || searchParams.has('error_description'))) {
      error = searchParams.get('error_description') || searchParams.get('error') || 
              'Error in authentication parameters';
    }
    
    if (env.DEV) {
      console.log('Token extraction:', { 
        hasAccessToken: !!accessToken,
        isRecoveryMode,
        hasCode,
        error
      });
    }
    
    return {
      accessToken,
      recoveryMode: isRecoveryMode,
      hasCode,
      error
    };
  } catch (err) {
    console.error('Error extracting token from URL:', err);
    return {
      accessToken: null,
      recoveryMode: false,
      hasCode: false,
      error: err instanceof Error ? err.message : 'Error extracting authentication parameters'
    };
  }
};

/**
 * Processes authentication tokens and codes, handling Supabase session exchange
 * Returns token data and status for auth flows
 */
export const processAuthTokens = async (tokenData: TokenData): Promise<TokenExtractResult> => {
  try {
    // Set initial values
    let accessToken = tokenData.accessToken;
    let errorMessage = tokenData.error;
    let isResetMode = false;
    
    // Log the token data we're processing
    console.log('Processing auth tokens:', {
      hasAccessToken: !!accessToken,
      recoveryMode: tokenData.recoveryMode,
      hasCode: tokenData.hasCode,
      hasError: !!errorMessage
    });
    
    // If we have an access token or recovery mode, we're in reset mode
    if (accessToken || tokenData.recoveryMode) {
      isResetMode = true;
      
      console.log('Direct token found, enabling reset mode');
      
      // Force setting up a Supabase session with the token if available
      if (accessToken) {
        try {
          // Try to set the session directly
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: ''
          });
          
          if (setSessionError) {
            console.warn('Error setting session with token:', setSessionError);
          } else {
            console.log('Successfully set session with token');
          }
        } catch (sessionError) {
          console.error('Error setting session:', sessionError);
        }
      }
      
      return { 
        tokenData: {
          ...tokenData,
          accessToken
        },
        errorMessage, 
        isResetMode 
      };
    }
    
    // If we have a code, try to exchange it for a session
    if (tokenData.hasCode && !errorMessage) {
      try {
        // Get the code from URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (!code) {
          throw new Error('Code parameter found but value is missing');
        }
        
        if (env.DEV) {
          console.log('Attempting to exchange code for session');
        }
        
        // Exchange code for session
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        
        if (error) {
          errorMessage = error.message || 'Error exchanging code for session';
          if (env.DEV) {
            console.error('Code exchange error:', error);
          }
        } else if (data?.session) {
          // Set access token from session
          accessToken = data.session.access_token;
          isResetMode = true;
          
          if (env.DEV) {
            console.log('Code exchange successful, received access token');
          }
        } else {
          errorMessage = 'No session returned from code exchange';
        }
      } catch (err) {
        errorMessage = err instanceof Error ? err.message : 'Error processing authentication code';
        console.error('Error processing auth code:', err);
      }
    }
    
    // If we don't have a token yet, check if there's an active session
    if (!accessToken && !errorMessage) {
      try {
        const { data } = await supabase.auth.getSession();
        
        if (data?.session) {
          accessToken = data.session.access_token;
          isResetMode = true;
          
          if (env.DEV) {
            console.log('Found active session, enabling reset mode');
          }
        }
      } catch (sessionErr) {
        console.error('Error checking for active session:', sessionErr);
      }
    }
    
    return {
      tokenData: {
        ...tokenData,
        accessToken
      },
      errorMessage,
      isResetMode
    };
  } catch (err) {
    console.error('Error processing auth tokens:', err);
    return {
      tokenData,
      errorMessage: err instanceof Error ? err.message : 'Error processing authentication',
      isResetMode: false
    };
  }
};

/**
 * Validates that the current URL is a valid reset password URL with necessary tokens
 */
export const validateResetPasswordUrl = (): boolean => {
  try {
    const tokenData = extractTokenFromUrl();
    const isValid = !!(tokenData.accessToken || tokenData.recoveryMode || tokenData.hasCode);
    
    console.log('Reset password URL validation:', {
      isValid,
      hasAccessToken: !!tokenData.accessToken,
      isRecoveryMode: tokenData.recoveryMode,
      hasCode: tokenData.hasCode
    });
    
    return isValid;
  } catch (err) {
    console.error('Error validating reset password URL:', err);
    return false;
  }
};

/**
 * Ensures the user is redirected to the appropriate page based on token type
 * This handles both password reset and email verification flows
 */
export const ensureResetPasswordPage = (): boolean => {
  try {
    const tokenData = extractTokenFromUrl();
    const hasResetParams = !!(tokenData.accessToken || tokenData.recoveryMode || tokenData.hasCode);
    const isOnResetPage = window.location.pathname.includes('reset-password');
    const isOnVerifyPage = window.location.pathname.includes('/verify');
    
    // Get URL parameters to check for token type
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const tokenType = urlParams.get('type') || hashParams.get('type');
    
    // Log the current state for debugging
    console.log('Auth redirect check:', {
      hasResetParams,
      isOnResetPage,
      isOnVerifyPage,
      tokenType,
      tokenData: {
        hasAccessToken: !!tokenData.accessToken,
        recoveryMode: tokenData.recoveryMode,
        hasCode: tokenData.hasCode
      },
      currentPath: window.location.pathname,
      currentUrl: window.location.href
    });
    
    // If this is a signup or email verification, redirect to verify page
    if (hasResetParams && (tokenType === 'signup' || tokenType === 'email') && !isOnVerifyPage) {
      const appDomain = window.location.origin;
      const verifyUrl = `${appDomain}/verify`;
      
      // Add the hash or search params as needed
      let redirectUrl = verifyUrl;
      
      if (tokenData.accessToken) {
        // For hash-based tokens
        redirectUrl = `${verifyUrl}${window.location.hash}`;
      } else if (tokenData.hasCode) {
        // For code-based tokens
        redirectUrl = `${verifyUrl}${window.location.search}`;
      }
      
      console.log('Redirecting to verify page:', redirectUrl);
      window.location.href = redirectUrl;
      return true; // Redirect initiated
    }
    
    // If this is a password reset and we're not on the reset page, redirect
    if (hasResetParams && (tokenType === 'recovery' || tokenData.recoveryMode) && !isOnResetPage && !isOnVerifyPage) {
      const appDomain = window.location.origin;
      const resetPasswordUrl = `${appDomain}/reset-password`;
      
      // Add the hash or search params as needed
      let redirectUrl = resetPasswordUrl;
      
      if (tokenData.accessToken) {
        // For hash-based tokens
        redirectUrl = `${resetPasswordUrl}${window.location.hash}`;
      } else if (tokenData.hasCode) {
        // For code-based tokens
        redirectUrl = `${resetPasswordUrl}${window.location.search}`;
      }
      
      console.log('Redirecting to reset password page:', redirectUrl);
      window.location.href = redirectUrl;
      return true; // Redirect initiated
    }
    
    return false; // No redirect needed
  } catch (err) {
    console.error('Error in ensureResetPasswordPage:', err);
    return false;
  }
};

export default {
  extractTokenFromUrl,
  processAuthTokens,
  validateResetPasswordUrl
}; 